/**
 * Page-number list query (#2736).
 *
 * `page` is 0-based. It resets when filters (stable JSON), sort, or page size
 * change, and clamps to the last page when `total` shrinks. Previous rows stay
 * on screen while the next page loads.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

/** Page sizes offered in list footers. The staff API caps a page at 200. */
export const ROWS_PER_PAGE_OPTIONS: readonly number[] = [25, 50, 100, 200];

const DEFAULT_ROWS_PER_PAGE = 50;

export type SortDirection = 'asc' | 'desc';

export interface PageSort {
  sortBy: string;
  sortDir: SortDirection;
}

export interface PagedFetchArgs<TFilters> {
  filters: TFilters;
  limit: number;
  offset: number;
  sortBy?: string;
  sortDir?: SortDirection;
}

export interface PagedRows<TRow> {
  data: TRow[];
  total: number;
}

/** Props for `EntityListResults`' `pagination`. */
export interface EntityListPagination {
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: readonly number[];
}

export interface UsePagedQueryOptions<TRow, TFilters> {
  queryKey: readonly unknown[];
  filters: TFilters;
  fetchPage: (args: PagedFetchArgs<TFilters>) => Promise<PagedRows<TRow>>;
  /** Initial page size. Defaults to 50. */
  defaultRowsPerPage?: number;
  /** Page sizes in the footer. Defaults to 25/50/100/200; the default size is always included. */
  rowsPerPageOptions?: readonly number[];
  /** Initial sort. Omitted sort leaves `sortBy` / `sortDir` off the request. */
  defaultSort?: PageSort;
  enabled?: boolean;
  staleTime?: number;
}

export interface UsePagedQueryResult<TRow> {
  rows: TRow[];
  total: number;
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  sort: PageSort | undefined;
  setSort: (sort: PageSort | undefined) => void;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  /** Pass straight to `EntityListResults`. */
  pagination: EntityListPagination;
}

interface PageScope {
  filtersKey: string;
  sortKey: string;
  rowsPerPage: number;
}

/**
 * Staff-api offset payload `{ data, pagination: { total, limit, offset, hasMore } }`
 * → the `{ data, total }` shape `fetchPage` returns.
 */
export function fromOffsetResponse<T>(res: {
  data: T[];
  pagination: { total: number };
}): PagedRows<T> {
  return { data: res.data, total: res.pagination.total };
}

export function usePagedQuery<TRow, TFilters>(
  options: UsePagedQueryOptions<TRow, TFilters>,
): UsePagedQueryResult<TRow> {
  const {
    queryKey,
    filters,
    fetchPage,
    defaultRowsPerPage = DEFAULT_ROWS_PER_PAGE,
    rowsPerPageOptions,
    defaultSort,
    enabled,
    staleTime,
  } = options;

  const [pageState, setPageState] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(defaultRowsPerPage);
  const [sort, setSortState] = useState<PageSort | undefined>(defaultSort);

  const filtersKey = stableStringify(filters);
  const sortKey = stableStringify(sort ?? null);
  const [scope, setScope] = useState<PageScope>({ filtersKey, sortKey, rowsPerPage });

  const scopeChanged =
    scope.filtersKey !== filtersKey ||
    scope.sortKey !== sortKey ||
    scope.rowsPerPage !== rowsPerPage;

  // Page size and sort are applied in this render so the request does not
  // leave with the previous offset.
  let visiblePage = scopeChanged ? 0 : pageState;
  const offset = visiblePage * rowsPerPage;
  const fetchArgs = buildFetchArgs(filters, rowsPerPage, offset, sort);

  const query = useQuery({
    queryKey: [
      ...queryKey,
      filters,
      {
        limit: rowsPerPage,
        offset,
        sortBy: sort?.sortBy,
        sortDir: sort?.sortDir,
      },
    ],
    queryFn: () => fetchPage(fetchArgs),
    placeholderData: keepPreviousData,
    ...(enabled !== undefined ? { enabled } : {}),
    ...(staleTime !== undefined ? { staleTime } : {}),
  });

  const total = query.data?.total ?? 0;
  // Placeholder rows belong to the previous key. Clamping against them would
  // move the page before the new total is known.
  if (query.isSuccess && !query.isPlaceholderData && query.data) {
    const lastPage = lastPageIndex(total, rowsPerPage);
    if (visiblePage > lastPage) visiblePage = lastPage;
  }

  if (scopeChanged) {
    setScope({ filtersKey, sortKey, rowsPerPage });
  }
  if (visiblePage !== pageState) {
    setPageState(visiblePage);
  }

  const setPage = useCallback((next: number) => {
    setPageState(next);
  }, []);

  const setRowsPerPage = useCallback((next: number) => {
    setRowsPerPageState(next);
  }, []);

  const setSort = useCallback((next: PageSort | undefined) => {
    setSortState(next);
  }, []);

  const pagination: EntityListPagination = {
    page: visiblePage,
    rowsPerPage,
    total,
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    rowsPerPageOptions: withPageSize(rowsPerPageOptions ?? ROWS_PER_PAGE_OPTIONS, defaultRowsPerPage),
  };

  return {
    rows: query.data?.data ?? [],
    total,
    page: visiblePage,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    sort,
    setSort,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
    pagination,
  };
}

/**
 * Local slice for lists that already hold every row (for example vehicle makes).
 * Page size changes return to the first page. A shorter list clamps the page.
 */
export function useClientPagination<T>(
  rows: readonly T[],
  defaultRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  rowsPerPageOptions: readonly number[] = ROWS_PER_PAGE_OPTIONS,
): { pageRows: T[]; pagination: EntityListPagination } {
  const [pageState, setPageState] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(defaultRowsPerPage);

  const lastPage = lastPageIndex(rows.length, rowsPerPage);
  const visiblePage = pageState > lastPage ? lastPage : pageState;
  if (visiblePage !== pageState) {
    setPageState(visiblePage);
  }

  const setPage = useCallback((next: number) => {
    setPageState(next);
  }, []);

  const setRowsPerPage = useCallback((next: number) => {
    setRowsPerPageState(next);
    setPageState(0);
  }, []);

  const start = visiblePage * rowsPerPage;

  return {
    pageRows: rows.slice(start, start + rowsPerPage),
    pagination: {
      page: visiblePage,
      rowsPerPage,
      total: rows.length,
      onPageChange: setPage,
      onRowsPerPageChange: setRowsPerPage,
      rowsPerPageOptions: withPageSize(rowsPerPageOptions, defaultRowsPerPage),
    },
  };
}

/**
 * MUI's page-size select shows an empty value (and warns) when the current
 * size is not one of its options, so the default size is always offered.
 */
function withPageSize(options: readonly number[], size: number): readonly number[] {
  return options.includes(size) ? options : [...options, size].sort((a, b) => a - b);
}

function lastPageIndex(total: number, rowsPerPage: number): number {
  if (!Number.isFinite(total) || !Number.isFinite(rowsPerPage) || rowsPerPage <= 0 || total <= 0) {
    return 0;
  }
  return Math.ceil(total / rowsPerPage) - 1;
}

function buildFetchArgs<TFilters>(
  filters: TFilters,
  limit: number,
  offset: number,
  sort: PageSort | undefined,
): PagedFetchArgs<TFilters> {
  const args: PagedFetchArgs<TFilters> = { filters, limit, offset };
  if (sort) {
    args.sortBy = sort.sortBy;
    args.sortDir = sort.sortDir;
  }
  return args;
}

/** Key order does not matter. A new object with the same fields is the same filter. */
function stableStringify(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null || typeof value === 'number' || typeof value === 'boolean') {
    return JSON.stringify(value) ?? 'null';
  }
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'bigint') return JSON.stringify(value.toString());
  if (typeof value !== 'object') return 'undefined';
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  const prototype = Object.getPrototypeOf(value) as object | null;
  if (prototype !== null && prototype !== Object.prototype) {
    return JSON.stringify(value) ?? 'undefined';
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(',')}}`;
}
